import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePaging } from 'hooks/usePaging';
import { SIZE_VALIDATION_ASSET_MODEL } from 'const/paging';
import {
  AssetIssueReportResponse,
  getAssetIssuesReport,
  ParamsGetAssetIssueReports
} from 'services/report';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { Report } from 'types/report';
import { fromPairs } from 'lodash';
import { CriticalityLevel, DEFAULT_CRITICALITY_LEVELS } from 'utils/issueCriticality';
import { exportAssetIssuesCsv } from '../utils/issuesListExport';

const NUMERIC_FILTER_KEYS = new Set(['clientId', 'locationId', 'assignedToUserId']);

type IssuesAssetView = 'active' | 'archived';

export const useIssuesAssetTab = (view: IssuesAssetView = 'active') => {
  const isArchivedView = view === 'archived';
  const [search, setSearch] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('');
  const [order, setOrder] = useState<'desc' | 'asc' | undefined>(undefined);
  const [page, setPage] = useState<number>(1);
  const [issues, setIssues] = useState<Report[]>([]);
  const [filterAssetIssues, setFilterAssetIssues] = useState<
    { key: string; value: string; label: string; level?: number }[] | undefined
  >();
  const [exporting, setExporting] = useState(false);

  const { pagingInfo, setCurrentPage, setTotal } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL
  });

  const buildFilterParams = useCallback((): Partial<ParamsGetAssetIssueReports> => {
    const raw = fromPairs(
      (filterAssetIssues ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    const params: Record<string, string | number> = {};
    Object.entries(raw).forEach(([key, value]) => {
      if (NUMERIC_FILTER_KEYS.has(key)) {
        const n = Number(value);
        if (!Number.isNaN(n)) params[key] = n;
      } else {
        params[key] = value as string;
      }
    });
    return params as Partial<ParamsGetAssetIssueReports>;
  }, [filterAssetIssues]);

  const fetchIssues = useCallback(
    async (params: Partial<ParamsGetAssetIssueReports>): Promise<AssetIssueReportResponse> => {
      const data = await getAssetIssuesReport(params);
      setIssues(data.reports);
      setTotal(data.total);
      return data;
    },
    [setTotal]
  );

  const { asyncCallback: asyncFetchIssues } = useAsyncCallback(fetchIssues, []);

  useEffect(() => {
    const params: Partial<ParamsGetAssetIssueReports> = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page,
      keyword: searchText || undefined,
      orderBy: sortBy || undefined,
      order,
      archived: isArchivedView,
      ...buildFilterParams()
    };
    asyncFetchIssues(params);
  }, [asyncFetchIssues, buildFilterParams, isArchivedView, order, page, searchText, sortBy]);

  const onSearch = useCallback(() => {
    setSearchText(search);
    setPage(1);
    setCurrentPage(1);
  }, [search, setCurrentPage]);

  const sortOption = useMemo(
    () => ({
      field: sortBy,
      order
    }),
    [order, sortBy]
  );

  const handleSort = useCallback(
    (orderBy: string) => {
      const sortByNow = sortBy;
      const orderNow = order;
      if (orderBy === sortByNow) {
        if (orderNow === 'asc') {
          setOrder('desc');
        } else {
          setOrder('asc');
        }
      } else {
        setSortBy(orderBy);
        setOrder('asc');
      }
    },
    [order, sortBy]
  );

  const handleChangePage = useCallback(
    (value: number) => {
      setCurrentPage(value);
      setPage(value);
    },
    [setCurrentPage]
  );

  const handleFilterAssetIssues = useCallback(
    (value?: { key: string; value: string; label: string; level?: number }) => {
      if (!value) return;
      setPage(1);
      setCurrentPage(1);
      setFilterAssetIssues((prevState) => {
        let nextState: typeof prevState;

        if (!prevState) {
          nextState = [value];
        } else {
          const index = prevState.findIndex((item) => item.key === value.key);
          if (index !== -1) {
            const existingItem = prevState[index];
            if (
              (!value.level && existingItem.value === value.value) ||
              Number(value.level) === Number(existingItem.level)
            ) {
              nextState = [...prevState.slice(0, index), ...prevState.slice(index + 1)];
            } else {
              const updated = [...prevState];
              updated[index] = value;
              nextState = updated;
            }
          } else {
            nextState = [...prevState, value];
          }
        }
        return nextState;
      });
    },
    [setCurrentPage]
  );

  const setToolbarFilter = useCallback(
    (key: string, value: string | undefined, label?: string) => {
      setPage(1);
      setCurrentPage(1);
      setFilterAssetIssues((prev) => {
        const list = [...(prev ?? [])];
        const idx = list.findIndex((f) => f.key === key);
        if (!value) {
          if (idx === -1) return prev;
          list.splice(idx, 1);
          return list.length ? list : undefined;
        }
        const entry = { key, value, label: label ?? value };
        if (idx === -1) list.push(entry);
        else list[idx] = entry;
        return list;
      });
    },
    [setCurrentPage]
  );

  const clearFilters = useCallback(() => {
    setFilterAssetIssues(undefined);
    setSearch('');
    setSearchText('');
    setPage(1);
    setCurrentPage(1);
  }, [setCurrentPage]);

  const refreshIssues = useCallback(() => {
    asyncFetchIssues({
      size: SIZE_VALIDATION_ASSET_MODEL,
      page,
      keyword: searchText || undefined,
      orderBy: sortBy || undefined,
      order,
      archived: isArchivedView,
      ...buildFilterParams()
    });
  }, [asyncFetchIssues, buildFilterParams, isArchivedView, order, page, searchText, sortBy]);

  const exportCsv = useCallback(
    async (criticalityLevels: CriticalityLevel[] = DEFAULT_CRITICALITY_LEVELS) => {
      setExporting(true);
      try {
        await exportAssetIssuesCsv(
          {
            keyword: searchText || undefined,
            orderBy: sortBy || undefined,
            order,
            archived: isArchivedView,
            ...buildFilterParams()
          },
          criticalityLevels
        );
      } finally {
        setExporting(false);
      }
    },
    [buildFilterParams, isArchivedView, order, searchText, sortBy]
  );

  const statusFilter = filterAssetIssues?.find((f) => f.key === 'status')?.value ?? '';
  const criticalityFilter = filterAssetIssues?.find((f) => f.key === 'criticalityKey')?.value ?? '';
  const assignedToFilter =
    filterAssetIssues?.find((f) => f.key === 'assignedToUserId')?.value ?? '';

  return {
    isArchivedView,
    search,
    setSearch,
    issues,
    pagingInfo,
    onSearch,
    sortOption,
    handleSort,
    handleChangePage,
    filterAssetIssues,
    setFilterAssetIssues: handleFilterAssetIssues,
    setToolbarFilter,
    clearFilters,
    statusFilter,
    criticalityFilter,
    assignedToFilter,
    refreshIssues,
    exportCsv,
    exporting
  };
};
